import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function PhotoCarousel({ photos = [], interval = 5000 }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback(
    (i) => setIndex(((i % photos.length) + photos.length) % photos.length),
    [photos.length]
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused || photos.length <= 1) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % photos.length),
      interval
    );
    return () => clearInterval(id);
  }, [paused, interval, photos.length]);

  if (!photos.length) return null;
  const current = photos[index];
  const showOverlay = current.eyebrow || current.caption;

  return (
    <div
      className="relative overflow-hidden border border-[#2a2a2a] group h-[440px] md:h-[560px] bg-[#0a0a0a]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="photo-carousel"
    >
      {photos.map((p, i) => (
        <img
          key={i}
          src={p.src}
          alt={p.alt || ""}
          className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${
            p.fit === "contain" ? "object-contain" : "object-cover"
          } ${i === index ? "opacity-100" : "opacity-0"}`}
          loading={i === 0 ? "eager" : "lazy"}
        />
      ))}

      {showOverlay && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-5 left-5 right-5 pointer-events-none">
            {current.eyebrow && (
              <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308] mb-2">
                {current.eyebrow}
              </div>
            )}
            {current.caption && (
              <div className="font-display text-2xl md:text-3xl text-white leading-tight whitespace-pre-line">
                {current.caption}
              </div>
            )}
          </div>
        </>
      )}

      {photos.length > 1 && (
        <>
          <button
            data-testid="carousel-prev"
            onClick={prev}
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#eab308] hover:text-black text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="carousel-next"
            onClick={next}
            aria-label="Next photo"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#eab308] hover:text-black text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronRight size={18} />
          </button>

          <div className="absolute top-4 right-4 flex gap-2">
            {photos.map((_, i) => (
              <button
                key={i}
                data-testid={`carousel-dot-${i}`}
                onClick={() => goTo(i)}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-1.5 transition-all ${
                  i === index
                    ? "w-8 bg-[#eab308]"
                    : "w-4 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
